import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, AlertCircle, Calendar, TrendingUp, FileText, Image, DollarSign, Clock, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { format, eachDayOfInterval, startOfDay, endOfDay, parseISO } from "date-fns";
import { Layout } from "@/components/layout/Layout";

interface PayrollEntry {
  id: string;
  user_id: string;
  period_start: string;
  period_end: string;
  amount: number;
  status: string;
  notes: string | null;
  galleries_count: number;
  blog_posts_count: number;
  total_posts: number;
  expected_posts: number;
  base_amount: number;
  adjustments_amount: number;
  performance_percentage: number;
  editor_name?: string;
}

interface DayData {
  date: Date;
  galleries: number;
  blogs: number;
  totalPosts: number;
  credits: number;
  creditAmount: number;
  dailyPay: number;
  adjustments: PayrollAdjustment[];
}

interface PayrollAdjustment {
  id: string;
  payroll_id: string | null;
  user_id: string;
  adjustment_type: string;
  post_credit: number;
  amount: number;
  notes: string | null;
  adjustment_date: string | null;
  created_at: string;
}

interface EditorRate {
  rate_per_post: number;
  posts_per_day: number;
}

interface PayrollDispute {
  id: string;
  payroll_id: string;
  description: string | null;
}

export default function PayrollDetail() {
  const { payrollId } = useParams<{ payrollId: string }>();
  const navigate = useNavigate();
  const { user, isAdmin, isLoading: authLoading } = useAuth();
  
  const [payrollEntry, setPayrollEntry] = useState<PayrollEntry | null>(null);
  const [dayData, setDayData] = useState<DayData[]>([]);
  const [editorRate, setEditorRate] = useState<EditorRate>({ rate_per_post: 5.71, posts_per_day: 14 });
  const [disputes, setDisputes] = useState<PayrollDispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDisputeDialogOpen, setIsDisputeDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [disputeNote, setDisputeNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Add Credit Dialog state (admin only)
  const [isAddCreditDialogOpen, setIsAddCreditDialogOpen] = useState(false);
  const [creditDate, setCreditDate] = useState<string>("");
  const [creditForm, setCreditForm] = useState({
    adjustment_type: "hour" as "hour" | "day" | "custom",
    custom_posts: "",
    notes: "",
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
      return;
    }
    
    if (payrollId) {
      fetchPayrollDetail();
    }
  }, [payrollId, user, authLoading]);

  const fetchPayrollDetail = async () => {
    if (!payrollId) return;
    
    try {
      // Fetch payroll entry
      const { data: payroll, error: payrollError } = await supabase
        .from("payroll")
        .select("*")
        .eq("id", payrollId)
        .single();

      if (payrollError) throw payrollError;

      // Security check - editors can only view their own payroll
      if (!isAdmin && payroll.user_id !== user?.id) {
        toast.error("Access denied");
        navigate("/admin");
        return;
      }

      setPayrollEntry(payroll);

      // Fetch editor rate
      const { data: rate } = await supabase
        .from("editor_rates")
        .select("rate_per_post, posts_per_day")
        .eq("user_id", payroll.user_id)
        .single();

      if (rate) {
        setEditorRate(rate);
      }

      // Fetch adjustments for this payroll
      const { data: adjustments } = await supabase
        .from("payroll_adjustments")
        .select("*")
        .eq("payroll_id", payrollId);

      // Fetch disputes for this payroll
      const { data: disputeData } = await supabase
        .from("payroll_disputes")
        .select("id, payroll_id, description")
        .eq("payroll_id", payrollId);
      
      setDisputes((disputeData as PayrollDispute[]) || []);

      // Get all days in the period
      const periodStart = parseISO(payroll.period_start);
      const periodEnd = parseISO(payroll.period_end);
      const allDays = eachDayOfInterval({ start: periodStart, end: periodEnd });

      // Fetch day-by-day data
      const dayDataPromises = allDays.map(async (day) => {
        const dayStart = startOfDay(day).toISOString();
        const dayEnd = endOfDay(day).toISOString();

        // Count galleries for this day
        const { count: galleriesCount } = await supabase
          .from("galleries")
          .select("*", { count: "exact", head: true })
          .eq("created_by", payroll.user_id)
          .gte("created_at", dayStart)
          .lte("created_at", dayEnd);

        // Count blog posts for this day
        const { count: blogsCount } = await supabase
          .from("blog_posts")
          .select("*", { count: "exact", head: true })
          .eq("created_by", payroll.user_id)
          .gte("created_at", dayStart)
          .lte("created_at", dayEnd);

        // Get adjustments for this day
        const dayAdjustments = (adjustments || []).filter((adj) => {
          if (adj.adjustment_date) {
            return format(parseISO(adj.adjustment_date), "yyyy-MM-dd") === format(day, "yyyy-MM-dd");
          }
          return false;
        });

        const totalCredits = dayAdjustments.reduce((sum, adj) => sum + adj.post_credit, 0);
        const creditAmount = dayAdjustments.reduce((sum, adj) => sum + adj.amount, 0);
        const totalPosts = (galleriesCount || 0) + (blogsCount || 0);
        const ratePerPost = rate?.rate_per_post || 5.71;

        return {
          date: day,
          galleries: galleriesCount || 0,
          blogs: blogsCount || 0,
          totalPosts,
          credits: totalCredits,
          creditAmount,
          dailyPay: totalPosts * ratePerPost + creditAmount,
          adjustments: dayAdjustments as PayrollAdjustment[],
        };
      });

      const resolvedDayData = await Promise.all(dayDataPromises);
      setDayData(resolvedDayData);
    } catch (error) {
      console.error("Error fetching payroll detail:", error);
      toast.error("Failed to load payroll details");
      navigate("/admin");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitDailyDispute = async () => {
    if (!disputeNote.trim()) {
      toast.error("Please provide a description for your dispute");
      return;
    }

    if (!selectedDate || !payrollEntry) return;

    setIsSubmitting(true);
    try {
      const dayInfo = dayData.find((d) => format(d.date, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd"));
      
      const description = `Daily Dispute for ${format(selectedDate, "MMMM d, yyyy")}\n\n` +
        `Galleries: ${dayInfo?.galleries || 0}\n` +
        `Blog Posts: ${dayInfo?.blogs || 0}\n` +
        `Total Posts: ${dayInfo?.totalPosts || 0}\n` +
        `Credits Applied: ${dayInfo?.credits || 0}\n` +
        `Daily Pay: $${dayInfo?.dailyPay?.toFixed(2) || "0.00"}\n\n` +
        `Issue: ${disputeNote}`;

      const { error } = await supabase.from("payroll_disputes").insert({
        payroll_id: payrollId,
        user_id: user?.id,
        dispute_reason: "daily_dispute",
        description,
        status: "pending",
      });

      if (error) throw error;

      // Send notification to admins
      try {
        const { data: adminRoles } = await supabase
          .from("user_roles")
          .select("user_id")
          .eq("role", "admin");

        for (const adminRole of adminRoles || []) {
          const { data: adminProfile } = await supabase
            .from("profiles")
            .select("email, full_name")
            .eq("id", adminRole.user_id)
            .single();

          if (adminProfile?.email) {
            await supabase.functions.invoke("send-payroll-notification", {
              body: {
                type: "dispute_filed",
                recipientEmail: adminProfile.email,
                recipientName: adminProfile.full_name || "Admin",
                data: {
                  editorName: payrollEntry.editor_name,
                  periodStart: payrollEntry.period_start,
                  periodEnd: payrollEntry.period_end,
                  disputeReason: disputeNote,
                  disputeDate: format(new Date(), "MMMM d, yyyy"),
                },
              },
            });
          }
        }
      } catch (notifError) {
        console.error("Failed to send dispute notification:", notifError);
      }

      toast.success("Dispute submitted successfully");
      setIsDisputeDialogOpen(false);
      setDisputeNote("");
      setSelectedDate(null);
      fetchPayrollDetail();
    } catch (error: any) {
      console.error("Error submitting dispute:", error);
      toast.error(error.message || "Failed to submit dispute");
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasDayDispute = (date: Date) => {
    const dateStr = format(date, "MMMM d, yyyy");
    return disputes.some((d) => d.description?.includes(dateStr));
  };

  // Handle Add Credit (admin only)
  const handleAddCredit = async () => {
    if (!creditForm.notes.trim()) {
      toast.error("Please provide a reason for the credit");
      return;
    }

    if (!payrollEntry || !creditDate) return;

    setIsSubmitting(true);
    try {
      let postCredit = 0;
      if (creditForm.adjustment_type === "hour") {
        postCredit = 2; // 1 hour = 2 posts
      } else if (creditForm.adjustment_type === "day") {
        postCredit = editorRate.posts_per_day || 14;
      } else {
        postCredit = parseInt(creditForm.custom_posts) || 0;
      }

      if (postCredit <= 0) {
        toast.error("Invalid credit amount");
        return;
      }

      const amount = postCredit * editorRate.rate_per_post;

      // Insert the adjustment
      const { error: insertError } = await supabase.from("payroll_adjustments").insert({
        payroll_id: payrollEntry.id,
        user_id: payrollEntry.user_id,
        adjustment_type: creditForm.adjustment_type,
        post_credit: postCredit,
        amount: amount,
        notes: creditForm.notes,
        adjustment_date: creditDate,
      });

      if (insertError) throw insertError;

      // Update payroll totals
      const newAdjustmentsAmount = (payrollEntry.adjustments_amount || 0) + amount;
      const newTotalAmount = (payrollEntry.base_amount || 0) + newAdjustmentsAmount;

      const { error: updateError } = await supabase
        .from("payroll")
        .update({
          adjustments_amount: newAdjustmentsAmount,
          amount: newTotalAmount,
        })
        .eq("id", payrollEntry.id);

      if (updateError) throw updateError;

      // Send notification to editor
      try {
        const { data: editorProfile } = await supabase
          .from("profiles")
          .select("email, full_name")
          .eq("id", payrollEntry.user_id)
          .single();

        if (editorProfile?.email) {
          await supabase.functions.invoke("send-payroll-notification", {
            body: {
              type: "credit_added",
              recipientEmail: editorProfile.email,
              recipientName: editorProfile.full_name || payrollEntry.editor_name,
              data: {
                creditPosts: postCredit,
                creditAmount: amount,
                creditDate: format(parseISO(creditDate), "MMMM d, yyyy"),
                creditNotes: creditForm.notes,
              },
            },
          });
        }
      } catch (notifError) {
        console.error("Failed to send credit notification:", notifError);
      }

      toast.success(`Added ${postCredit} post credit${postCredit > 1 ? "s" : ""} (+$${amount.toFixed(2)})`);
      setIsAddCreditDialogOpen(false);
      setCreditForm({ adjustment_type: "hour", custom_posts: "", notes: "" });
      setCreditDate("");
      fetchPayrollDetail();
    } catch (error: any) {
      console.error("Error adding credit:", error);
      toast.error(error.message || "Failed to add credit");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPerformanceBadge = (percentage: number) => {
    if (percentage >= 100) {
      return <Badge className="bg-green-100 text-green-700 border-green-200">{percentage}% ✓</Badge>;
    } else if (percentage >= 75) {
      return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">{percentage}%</Badge>;
    } else {
      return <Badge className="bg-red-100 text-red-700 border-red-200">{percentage}%</Badge>;
    }
  };

  // Calculate totals
  const totals = dayData.reduce(
    (acc, day) => ({
      galleries: acc.galleries + day.galleries,
      blogs: acc.blogs + day.blogs,
      totalPosts: acc.totalPosts + day.totalPosts,
      credits: acc.credits + day.credits,
      creditAmount: acc.creditAmount + day.creditAmount,
      dailyPay: acc.dailyPay + day.dailyPay,
    }),
    { galleries: 0, blogs: 0, totalPosts: 0, credits: 0, creditAmount: 0, dailyPay: 0 }
  );

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </Layout>
    );
  }

  if (!payrollEntry) {
    return (
      <Layout>
        <div className="container py-8">
          <p className="text-center text-muted-foreground">Payroll entry not found</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container py-8 space-y-6">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate("/admin?tab=payroll")} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Payroll
        </Button>

        {/* Header Card */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <CardTitle className="text-2xl font-serif">
                  {payrollEntry.editor_name || "Editor"}'s Payroll
                </CardTitle>
                <CardDescription className="flex items-center gap-2 mt-1">
                  <Calendar className="h-4 w-4" />
                  {format(parseISO(payrollEntry.period_start), "MMMM d")} - {format(parseISO(payrollEntry.period_end), "MMMM d, yyyy")}
                </CardDescription>
              </div>
              <Badge 
                className={payrollEntry.status === "paid" 
                  ? "bg-green-100 text-green-700 border-green-200" 
                  : "bg-yellow-100 text-yellow-700 border-yellow-200"
                }
              >
                {payrollEntry.status === "paid" ? "Paid" : "Pending"}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-4">
              <div className="bg-muted rounded-lg p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <TrendingUp className="h-4 w-4" />
                  Expected Posts
                </div>
                <p className="text-2xl font-bold">{payrollEntry.expected_posts}</p>
              </div>
              <div className="bg-muted rounded-lg p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <FileText className="h-4 w-4" />
                  Actual Posts
                </div>
                <p className="text-2xl font-bold">{payrollEntry.total_posts}</p>
              </div>
              <div className="bg-muted rounded-lg p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <TrendingUp className="h-4 w-4" />
                  Performance
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">{payrollEntry.performance_percentage}%</p>
                  {getPerformanceBadge(payrollEntry.performance_percentage)}
                </div>
                <Progress 
                  value={Math.min(payrollEntry.performance_percentage, 100)} 
                  className="mt-2 h-2"
                />
              </div>
              <div className="bg-muted rounded-lg p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <DollarSign className="h-4 w-4" />
                  Total Pay
                </div>
                <p className="text-2xl font-bold text-green-600">${payrollEntry.amount.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Day-by-Day Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Day-by-Day Breakdown</CardTitle>
            <CardDescription>Detailed view of posts and earnings for each day</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Image className="h-4 w-4" />
                        Galleries
                      </div>
                    </TableHead>
                    <TableHead className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <FileText className="h-4 w-4" />
                        Blog Posts
                      </div>
                    </TableHead>
                    <TableHead className="text-center">Total Posts</TableHead>
                    <TableHead className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Clock className="h-4 w-4" />
                        Credits
                      </div>
                    </TableHead>
                    <TableHead className="text-right">Daily Pay</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dayData.map((day) => {
                    const isWeekend = day.date.getDay() === 0 || day.date.getDay() === 6;
                    const hasDispute = hasDayDispute(day.date);
                    
                    return (
                      <TableRow 
                        key={format(day.date, "yyyy-MM-dd")}
                        className={isWeekend ? "bg-muted/30" : ""}
                      >
                        <TableCell className="font-medium">
                          <div>
                            {format(day.date, "EEE, MMM d")}
                            {isWeekend && (
                              <Badge variant="outline" className="ml-2 text-xs">Weekend</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {day.galleries > 0 ? (
                            <span className="font-medium">{day.galleries}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {day.blogs > 0 ? (
                            <span className="font-medium">{day.blogs}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center font-medium">
                          {day.totalPosts > 0 ? day.totalPosts : "—"}
                        </TableCell>
                        <TableCell className="text-center">
                          {day.credits > 0 ? (
                            <div className="group relative inline-block">
                              <Badge className="bg-green-100 text-green-700 border-green-200 cursor-help">
                                +{day.credits}
                              </Badge>
                              {day.adjustments.length > 0 && (
                                <div className="absolute left-1/2 -translate-x-1/2 top-full z-50 hidden group-hover:block mt-1 w-48 rounded-md border bg-popover p-2 shadow-lg text-xs">
                                  {day.adjustments.map((adj) => (
                                    <div key={adj.id} className="py-1 border-b last:border-0">
                                      <div className="flex justify-between">
                                        <span>{adj.adjustment_type === "hour" ? "+1 Hour" : adj.adjustment_type === "day" ? "+1 Day" : `+${adj.post_credit}`}</span>
                                        <span className="text-green-600">+${adj.amount.toFixed(2)}</span>
                                      </div>
                                      {adj.notes && <p className="text-muted-foreground truncate">{adj.notes}</p>}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {day.dailyPay > 0 ? (
                            <span className="font-medium">${day.dailyPay.toFixed(2)}</span>
                          ) : (
                            <span className="text-muted-foreground">$0.00</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {isAdmin && payrollEntry.status === "pending" && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setCreditDate(format(day.date, "yyyy-MM-dd"));
                                  setIsAddCreditDialogOpen(true);
                                }}
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                Add Credit
                              </Button>
                            )}
                            {hasDispute ? (
                              <Badge variant="outline" className="text-yellow-600">Disputed</Badge>
                            ) : payrollEntry.status === "pending" ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedDate(day.date);
                                  setIsDisputeDialogOpen(true);
                                }}
                              >
                                <AlertCircle className="h-4 w-4 mr-1" />
                                Dispute
                              </Button>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Totals Summary */}
            <div className="mt-6 rounded-lg bg-muted p-4">
              <div className="grid gap-4 md:grid-cols-6">
                <div>
                  <p className="text-sm text-muted-foreground">Total Galleries</p>
                  <p className="text-xl font-bold">{totals.galleries}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Blog Posts</p>
                  <p className="text-xl font-bold">{totals.blogs}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Posts</p>
                  <p className="text-xl font-bold">{totals.totalPosts}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Credits</p>
                  <p className="text-xl font-bold text-green-600">+{totals.credits}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Base Pay</p>
                  <p className="text-xl font-bold">${payrollEntry.base_amount.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Grand Total</p>
                  <p className="text-2xl font-bold text-green-600">${payrollEntry.amount.toFixed(2)}</p>
                </div>
              </div>
              {totals.creditAmount > 0 && (
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Time Credits Applied</span>
                    <span className="font-medium text-green-600">+${totals.creditAmount.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Daily Dispute Dialog */}
        <Dialog open={isDisputeDialogOpen} onOpenChange={setIsDisputeDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Dispute Day</DialogTitle>
              <DialogDescription>
                {selectedDate && `Submit a dispute for ${format(selectedDate, "EEEE, MMMM d, yyyy")}`}
              </DialogDescription>
            </DialogHeader>
            {selectedDate && (() => {
              const dayInfo = dayData.find((d) => format(d.date, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd"));
              return (
                <div className="space-y-4 py-4">
                  <div className="rounded-lg bg-muted p-3 text-sm">
                    <div className="grid grid-cols-2 gap-2">
                      <div>Galleries: <strong>{dayInfo?.galleries || 0}</strong></div>
                      <div>Blog Posts: <strong>{dayInfo?.blogs || 0}</strong></div>
                      <div>Total Posts: <strong>{dayInfo?.totalPosts || 0}</strong></div>
                      <div>Credits: <strong className="text-green-600">+{dayInfo?.credits || 0}</strong></div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-border">
                      Daily Pay: <strong>${dayInfo?.dailyPay?.toFixed(2) || "0.00"}</strong>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>What's the issue?</Label>
                    <Textarea
                      value={disputeNote}
                      onChange={(e) => setDisputeNote(e.target.value)}
                      placeholder="Explain what needs to be corrected for this day..."
                      rows={4}
                    />
                  </div>
                </div>
              );
            })()}
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIsDisputeDialogOpen(false);
                setDisputeNote("");
                setSelectedDate(null);
              }}>
                Cancel
              </Button>
              <Button onClick={handleSubmitDailyDispute} disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Submit Dispute"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Credit Dialog (Admin Only) */}
        <Dialog open={isAddCreditDialogOpen} onOpenChange={setIsAddCreditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Time Credit</DialogTitle>
              <DialogDescription>
                {creditDate && `Adding credit for ${format(parseISO(creditDate), "EEEE, MMMM d, yyyy")}`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Credit Type</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={creditForm.adjustment_type === "hour" ? "default" : "outline"}
                    className="flex-1"
                    onClick={() => setCreditForm({ ...creditForm, adjustment_type: "hour", custom_posts: "" })}
                  >
                    +1 Hour (2 posts)
                  </Button>
                  <Button
                    type="button"
                    variant={creditForm.adjustment_type === "day" ? "default" : "outline"}
                    className="flex-1"
                    onClick={() => setCreditForm({ ...creditForm, adjustment_type: "day", custom_posts: "" })}
                  >
                    +1 Day ({editorRate.posts_per_day} posts)
                  </Button>
                  <Button
                    type="button"
                    variant={creditForm.adjustment_type === "custom" ? "default" : "outline"}
                    className="flex-1"
                    onClick={() => setCreditForm({ ...creditForm, adjustment_type: "custom" })}
                  >
                    Custom
                  </Button>
                </div>
              </div>

              {creditForm.adjustment_type === "custom" && (
                <div className="space-y-2">
                  <Label>Number of Posts</Label>
                  <Input
                    type="number"
                    min="1"
                    value={creditForm.custom_posts}
                    onChange={(e) => setCreditForm({ ...creditForm, custom_posts: e.target.value })}
                    placeholder="Enter number of posts"
                  />
                  {creditForm.custom_posts && (
                    <p className="text-sm text-muted-foreground">
                      = ${(parseInt(creditForm.custom_posts) * editorRate.rate_per_post).toFixed(2)}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label>Reason (required)</Label>
                <Textarea
                  value={creditForm.notes}
                  onChange={(e) => setCreditForm({ ...creditForm, notes: e.target.value })}
                  placeholder="Why is this credit being added?"
                  rows={3}
                />
              </div>

              <div className="rounded-lg bg-muted p-3 text-sm">
                <div className="flex justify-between">
                  <span>Credit Amount:</span>
                  <span className="font-medium text-green-600">
                    +{creditForm.adjustment_type === "hour" 
                      ? 2 
                      : creditForm.adjustment_type === "day" 
                        ? editorRate.posts_per_day 
                        : parseInt(creditForm.custom_posts) || 0} posts 
                    (${(
                      (creditForm.adjustment_type === "hour" 
                        ? 2 
                        : creditForm.adjustment_type === "day" 
                          ? editorRate.posts_per_day 
                          : parseInt(creditForm.custom_posts) || 0
                      ) * editorRate.rate_per_post
                    ).toFixed(2)})
                  </span>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIsAddCreditDialogOpen(false);
                setCreditForm({ adjustment_type: "hour", custom_posts: "", notes: "" });
                setCreditDate("");
              }}>
                Cancel
              </Button>
              <Button onClick={handleAddCredit} disabled={isSubmitting}>
                {isSubmitting ? "Adding..." : "Add Credit"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
